import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useWorkspaceContext } from "../../../providers/WorkspaceProvider";
import { useDigestResultGet } from "../hooks";
import type {
  DigestResult,
  DigestItem,
  DigestSentiment,
} from "../types/digest";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Table,
  ToggleButtonGroup,
  Typography,
} from "@mui/joy";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import Loading from "../../../components/Loading";
import PageHeader from "../../../components/PageHeader";
import Page from "../../../components/Pages/Page";
import { FloatingChatWidget } from "../components/BKGAgent";
import { stripSourceRefs } from "../../../utils/stripSourceRefs";
import { convertToCSV, downloadCSV } from "../utils/exportUtils";
import {
  COMPANY_PROFILE_BACK_SOURCE,
  buildCompanyHref,
} from "../utils/profileBackNavigation";

const SENTIMENT_DISPLAY: Record<
  DigestSentiment,
  { label: string; color: "success" | "danger" | "neutral" }
> = {
  positive: { label: "Positive", color: "success" },
  negative: { label: "Negative", color: "danger" },
  neutral: { label: "Neutral", color: "neutral" },
};

const SentimentChip: React.FC<{ sentiment: DigestSentiment }> = ({
  sentiment,
}) => (
  <Chip size="sm" variant="soft" color={SENTIMENT_DISPLAY[sentiment].color}>
    {SENTIMENT_DISPLAY[sentiment].label}
  </Chip>
);

type DigestSortOrder = "alphabetical" | "rank";

/** A-Z by company (then headline); items without a company name go last. */
export function sortDigestItems(
  items: DigestItem[],
  order: DigestSortOrder,
): DigestItem[] {
  const sorted = [...items];
  if (order === "rank") {
    return sorted.sort((a, b) => a.rank - b.rank);
  }
  return sorted.sort((a, b) => {
    if (!a.company_name !== !b.company_name) return a.company_name ? -1 : 1;
    return (
      (a.company_name ?? "").localeCompare(b.company_name ?? "", undefined, {
        sensitivity: "base",
      }) ||
      (a.title ?? "").localeCompare(b.title ?? "", undefined, {
        sensitivity: "base",
      })
    );
  });
}

const DigestView: React.FC = () => {
  const navigate = useNavigate();
  const { workspace } = useWorkspaceContext();
  const { result_id } = useParams<{
    config_id: string;
    result_id: string;
  }>();

  const [result, setResult] = useState<DigestResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<DigestSortOrder>("alphabetical");

  const fetchResult = useDigestResultGet();

  useEffect(() => {
    if (!workspace?.id || !result_id) return;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchResult(workspace.id, result_id);
        setResult(data);
      } catch (err) {
        console.error("Error loading digest result:", err);
        setError("Failed to load digest result.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [workspace, result_id, fetchResult]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleDownloadCSV = () => {
    if (!result) return;
    const rows = sortedItems.map((item) => ({
      Rank: String(item.rank),
      "Reported Date": item.reported_date ?? "",
      "Event Date": item.event_date ?? "",
      Company: item.company_name ?? "",
      Headline: item.title ?? "",
      Summary: item.summary ? stripSourceRefs(item.summary) : "",
      Rationale: item.rationale ? stripSourceRefs(item.rationale) : "",
      Signal: item.sentiment ? SENTIMENT_DISPLAY[item.sentiment].label : "",
      "Source (URL)": item.source_url ?? "",
    }));
    const csv = convertToCSV(rows);
    const dateStr = result.generated_at
      ? new Date(result.generated_at).toISOString().slice(0, 10)
      : "export";
    downloadCSV(csv, `catalyst_digest_${dateStr}.csv`);
  };

  // `company_id` is the full BKG document id ("<workspace>/company/<slug>")
  // resolved server-side from the catalyst event's company reference. Items
  // whose event has no resolvable company render as plain text instead.
  const getCompanyHref = (item: DigestItem): string | undefined => {
    if (!workspace?.id || !item.company_id) return undefined;
    return buildCompanyHref(workspace.id, item.company_id, {
      from: COMPANY_PROFILE_BACK_SOURCE.catalystEvents,
    });
  };

  const handleCompanyClick = (item: DigestItem) => {
    const href = getCompanyHref(item);
    if (!href || !item.company_id) return;
    // Also carry the full document id in state so in-app navigation skips URL
    // parsing; the href itself already resolves (slash-containing ids are
    // encoded whole), which covers new tabs and copied links.
    navigate(href, { state: { documentId: item.company_id } });
  };

  const executiveSummary = result?.executive_summary ?? [];
  const sortedItems = useMemo(
    () => sortDigestItems(result?.items ?? [], sortOrder),
    [result, sortOrder],
  );
  const sentimentItems = sortedItems.filter((item) => item.sentiment);

  if (!workspace?.id) {
    return (
      <Page>
        <Alert color="danger" variant="soft">
          Workspace not specified.
        </Alert>
      </Page>
    );
  }

  if (loading) {
    return (
      <Page>
        <Loading />
      </Page>
    );
  }

  if (error || !result) {
    return (
      <Page>
        <Alert color="danger" variant="soft">
          {error || "Digest result not found."}
        </Alert>
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ minWidth: 0, flexWrap: "wrap" }}
        >
          <Button
            variant="plain"
            color="neutral"
            size="sm"
            startDecorator={<ArrowBackIcon />}
            onClick={() =>
              navigate(`/workspace/${workspace.id}/bkg/catalyst-events/digests`)
            }
          >
            Back
          </Button>
          <Box sx={{ minWidth: 0 }}>
            <Typography level="h1">Catalyst Digest</Typography>
            <Typography level="body-sm" color="neutral">
              Generated {formatDate(result.generated_at)}
              {" -- "}
              {result.event_count} events analysed
            </Typography>
          </Box>
        </Stack>
        <Button
          variant="outlined"
          color="neutral"
          size="sm"
          startDecorator={<DownloadIcon />}
          onClick={handleDownloadCSV}
        >
          Download
        </Button>
      </PageHeader>

      <Stack spacing={3}>
        {/* Error banner */}
        {result.status === "error" && (
          <Alert color="danger" variant="soft">
            Generation encountered an error: {result.error_message}
          </Alert>
        )}

        {/* Meta info */}
        <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap" }}>
          {result.llm_model && result.llm_model !== "N/A" && (
            <Chip size="sm" variant="outlined">
              Model: {result.llm_model}
            </Chip>
          )}
          {result.generation_duration_seconds != null && (
            <Chip size="sm" variant="outlined">
              {result.generation_duration_seconds.toFixed(1)}s
            </Chip>
          )}
          {result.generated_by && (
            <Chip size="sm" variant="outlined">
              {result.generated_by.startsWith("manual:")
                ? "Manual trigger"
                : "Scheduled"}
            </Chip>
          )}
        </Stack>

        {/* Executive snapshot */}
        {executiveSummary.length > 0 && (
          <Card variant="soft" color="primary">
            <CardContent>
              <Typography level="title-lg" sx={{ mb: 1 }}>
                Executive snapshot
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {executiveSummary.map((point, index) => (
                  <Typography
                    key={index}
                    component="li"
                    level="body-md"
                    sx={{ mb: 0.5 }}
                  >
                    {stripSourceRefs(point)}
                  </Typography>
                ))}
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Items */}
        {result.items.length === 0 ? (
          <Card variant="outlined">
            <CardContent>
              <Typography
                level="body-md"
                color="neutral"
                sx={{ textAlign: "center", py: 4 }}
              >
                No events matched the configured filters.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Stack spacing={2}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="flex-end"
            >
              <Typography level="body-sm" color="neutral">
                Sort by
              </Typography>
              <ToggleButtonGroup
                size="sm"
                value={sortOrder}
                onChange={(_e, value) => {
                  if (value) setSortOrder(value as DigestSortOrder);
                }}
                aria-label="Sort digest items"
              >
                <Button value="alphabetical">Company A–Z</Button>
                <Button value="rank">Rank</Button>
              </ToggleButtonGroup>
            </Stack>
            {sortedItems.map((item, index) => (
              <DigestItemCard
                key={index}
                item={item}
                companyHref={getCompanyHref(item)}
                onCompanyClick={handleCompanyClick}
              />
            ))}
          </Stack>
        )}

        {/* Company sentiment summary */}
        {sentimentItems.length > 0 && (
          <Card variant="outlined">
            <CardContent>
              <Typography level="title-lg" sx={{ mb: 1 }}>
                Company sentiment summary
              </Typography>
              <Box sx={{ overflowX: "auto" }}>
                <Table size="sm" sx={{ minWidth: 480 }}>
                  <thead>
                    <tr>
                      <th style={{ width: "28%" }}>Company</th>
                      <th>Key development</th>
                      <th style={{ width: 110 }}>Signal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sentimentItems.map((item, index) => (
                      <tr key={index}>
                        <td>{item.company_name ?? ""}</td>
                        <td>{item.title ?? ""}</td>
                        <td>
                          <SentimentChip sentiment={item.sentiment!} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Box>
              <Typography level="body-xs" color="neutral" sx={{ mt: 1 }}>
                Positive and negative labels reflect the direction of the
                announced development, not investment recommendations.
              </Typography>
            </CardContent>
          </Card>
        )}
      </Stack>
      <FloatingChatWidget workspaceId={workspace?.id ?? ""} />
    </Page>
  );
};

interface DigestItemCardProps {
  item: DigestItem;
  /** Undefined when the item has no linkable company. */
  companyHref?: string;
  onCompanyClick: (item: DigestItem) => void;
}

const DigestItemCard: React.FC<DigestItemCardProps> = ({
  item,
  companyHref,
  onCompanyClick,
}) => {
  return (
    <Card
      variant="outlined"
      sx={{
        transition: "all 0.2s ease",
        "&:hover": {
          boxShadow: "sm",
          borderColor: "primary.300",
        },
      }}
    >
      <CardContent>
        <Stack spacing={1.5}>
          {/* Rank + Company Name */}
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Box
              sx={{
                minWidth: 36,
                height: 36,
                borderRadius: "sm",
                bgcolor: "primary.softBg",
                color: "primary.plainColor",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: "0.875rem",
                flexShrink: 0,
              }}
            >
              {item.rank}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {item.company_name &&
                (companyHref ? (
                  // A real anchor, so Cmd/Ctrl/middle-click opens a new tab.
                  <Typography
                    component="a"
                    href={companyHref}
                    level="title-md"
                    sx={{
                      fontWeight: 600,
                      color: "inherit",
                      textDecoration: "none",
                      cursor: "pointer",
                      "&:hover": { textDecoration: "underline" },
                    }}
                    onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
                      // Let the browser handle modified clicks natively.
                      if (
                        event.metaKey ||
                        event.ctrlKey ||
                        event.shiftKey ||
                        event.altKey ||
                        event.button !== 0
                      ) {
                        return;
                      }
                      event.preventDefault();
                      onCompanyClick(item);
                    }}
                  >
                    {item.company_name}
                  </Typography>
                ) : (
                  <Typography level="title-md" sx={{ fontWeight: 600 }}>
                    {item.company_name}
                  </Typography>
                ))}
              {item.title && (
                <Typography level="body-md" sx={{ fontWeight: 600 }}>
                  {item.title}
                </Typography>
              )}
            </Box>
          </Stack>

          {/* Type + Date + Reported + Source */}
          <Stack
            direction="row"
            spacing={1}
            sx={{ flexWrap: "wrap", gap: 0.5, alignItems: "center" }}
          >
            {item.event_type && (
              <Chip size="sm" variant="soft" color="primary">
                {item.event_type}
              </Chip>
            )}
            {item.sentiment && <SentimentChip sentiment={item.sentiment} />}
            {item.event_date && (
              <Chip size="sm" variant="outlined">
                {item.event_date}
              </Chip>
            )}
            {item.reported_date && (
              <Chip size="sm" variant="plain" color="neutral">
                Reported {item.reported_date}
              </Chip>
            )}
            {item.source_url && (
              <Chip
                size="sm"
                variant="outlined"
                color="neutral"
                endDecorator={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                slotProps={{
                  action: {
                    component: "a",
                    href: item.source_url,
                    target: "_blank",
                    rel: "noopener noreferrer",
                  },
                }}
                sx={{ cursor: "pointer" }}
              >
                Source
              </Chip>
            )}
          </Stack>

          {/* Summary */}
          {item.summary && (
            <Typography level="body-sm">
              {stripSourceRefs(item.summary)}
            </Typography>
          )}

          {/* Rationale */}
          {item.rationale && (
            <Box
              sx={{
                bgcolor: "background.level1",
                borderRadius: "sm",
                p: 1.5,
                borderLeft: "3px solid",
                borderColor: "primary.300",
              }}
            >
              <Typography level="body-xs" sx={{ fontWeight: 600, mb: 0.5 }}>
                Why this matters:
              </Typography>
              <Typography level="body-sm" sx={{ fontStyle: "italic" }}>
                {stripSourceRefs(item.rationale)}
              </Typography>
            </Box>
          )}

          {/* Source URL now displayed as chip in the Type+Date row above */}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default DigestView;
